import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, Plus, Trash2, Save, RotateCcw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { adminGetNews, adminPutNews, adminResetNews } from "@/lib/api";

const TAGS = ["Launch", "AI", "Demos", "Leads", "Payments"];
const input = "w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-brand outline-none";

export default function AdminWhatsNew() {
  const [items, setItems] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { adminGetNews().then((d) => setItems(d.items || [])).catch(() => toast.error("Failed to load")); }, []);

  const set = (i, patch) => setItems((arr) => arr.map((x, xi) => xi === i ? { ...x, ...patch } : x));
  const add = () => setItems((arr) => [{ date: new Date().toISOString().slice(0, 10), tag: "Launch", title: "New update", desc: "", to: "" }, ...arr]);
  const del = (i) => setItems((arr) => arr.filter((_, xi) => xi !== i));

  const save = async () => {
    setSaving(true);
    try { await adminPutNews({ items }); toast.success("Saved — live on /whats-new"); }
    catch { toast.error("Save failed"); } finally { setSaving(false); }
  };
  const reset = async () => {
    if (!window.confirm("Restore the default What's New feed?")) return;
    try { const d = await adminResetNews(); setItems(d.items); toast.success("Reset to default"); } catch { toast.error("Reset failed"); }
  };

  if (!items) return <main className="min-h-screen bg-slate-50 grid place-items-center"><Loader2 className="animate-spin text-brand" size={26} /></main>;

  return (
    <main className="min-h-screen bg-slate-50 pb-24" data-testid="admin-whats-new">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to="/admin" className="text-slate-500 hover:text-brand text-sm flex items-center gap-1.5 mb-3"><ArrowLeft size={14} /> Back to console</Link>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2"><Sparkles size={22} className="text-brand" /><h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">What's New Editor</h1></div>
          <div className="flex items-center gap-2">
            <button onClick={reset} data-testid="news-reset" className="inline-flex items-center gap-1.5 btn-outline rounded-lg px-4 py-2.5 text-sm"><RotateCcw size={15} /> Reset</button>
            <a href="/whats-new" target="_blank" rel="noopener noreferrer" className="text-sm text-slate-500 hover:text-brand px-2">Preview ↗</a>
            <button onClick={save} disabled={saving} data-testid="news-save" className="inline-flex items-center gap-1.5 bg-brand text-white rounded-lg px-5 py-2.5 text-sm font-semibold disabled:opacity-60">{saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save</button>
          </div>
        </div>
        <button onClick={add} data-testid="news-add" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-brand"><Plus size={15} /> Add update</button>

        <div className="space-y-3 mt-4">
          {items.map((n, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-xl card-shadow p-4" data-testid={`news-editor-${i}`}>
              <div className="flex items-start gap-2">
                <input value={n.title} onChange={(e) => set(i, { title: e.target.value })} placeholder="Title" className={`${input} font-semibold`} />
                <button onClick={() => del(i)} className="text-slate-300 hover:text-red-500 p-2 shrink-0"><Trash2 size={16} /></button>
              </div>
              <div className="grid sm:grid-cols-3 gap-2 mt-2">
                <input type="date" value={n.date} onChange={(e) => set(i, { date: e.target.value })} className={input} />
                <select value={n.tag} onChange={(e) => set(i, { tag: e.target.value })} className={input}>{TAGS.map((t) => <option key={t}>{t}</option>)}</select>
                <input value={n.to || ""} onChange={(e) => set(i, { to: e.target.value })} placeholder="Link e.g. /tools" className={input} />
              </div>
              <textarea value={n.desc || ""} onChange={(e) => set(i, { desc: e.target.value })} placeholder="Description" rows={2} className={`${input} mt-2 resize-none`} />
            </div>
          ))}
        </div>
      </div>
    </main>
  );
}
